// app/apps/campsite-explorer/components/PopularRoutes.tsx
'use client';

import { withBase } from '../lib/brand';
import CaseSwap from './store/CaseSwap';
import { useCaPreview } from '../lib/caPreview';
import React from 'react';
import { useRouter } from 'next/navigation';
import { SwapOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { FaRoute, FaCar, FaClock, FaStar } from 'react-icons/fa';

interface PopularRoute {
  id: string;
  name: string;
  description: string;
  from: string;
  to: string;
  distance: string;
  duration: string;
  route_type: 'coastal' | 'inland' | 'mountain' | 'outback' | 'scenic' | 'highway';
  highlights: string[];
  campsite_count?: number;
  distance_from_campsite?: number;
  popularity?: number;
  passes_through_town?: boolean;
  /** Gallery photos from campsites along this route (our own imagery). */
  collage_images?: string[];
  /** Attribution (author / license) for any Wikimedia Commons fill images. */
  collage_credits?: string[];
}

interface PopularRoutesProps {
  routes: PopularRoute[];
  currentLocation?: string;
  /** This campsite's own photo (it's on the route) — used as the hero for the
   *  featured card. Permitted: it's already displayed on this page. */
  heroImage?: string;
  onRouteSelect?: (from: string, to: string, routeName: string) => void;
}

const getRouteTypeIcon = (type: string): string => {
  switch (type) {
    case 'coastal': return '🏖️';
    case 'mountain': return '⛰️';
    case 'outback': return '🏜️';
    case 'scenic': return '🌄';
    default: return '🛣️';
  }
};

const getRouteGradient = (type: string): string => {
  switch (type) {
    case 'coastal': return 'from-accent to-cyan-400';
    case 'mountain': return 'from-green-600 to-emerald-500';
    case 'outback': return 'from-orange-500 to-red-400';
    case 'scenic': return 'from-purple-500 to-pink-400';
    default: return 'from-gray-600 to-gray-500';
  }
};

const getRouteChipBg = (type: string): string => {
  switch (type) {
    case 'coastal': return 'bg-cyan-600/85';
    case 'mountain': return 'bg-emerald-700/85';
    case 'outback': return 'bg-orange-600/85';
    case 'scenic': return 'bg-purple-600/85';
    default: return 'bg-slate-700/85';
  }
};

/**
 * Image-led road-trip card with a 1-big + 2-small collage. Tracks images that
 * fail to load (some on-route Bookeasy thumbnails 404) and recomposes the
 * collage from the survivors, falling back to a themed gradient if none load.
 */
function RouteCard({ route, heroImage, currentLocation, onClick, onPlanTrip }: {
  route: PopularRoute;
  heroImage?: string;
  currentLocation?: string;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onPlanTrip: (e: React.SyntheticEvent) => void;
}): JSX.Element {
  const [failed, setFailed] = React.useState<Record<string, boolean>>({});
  // ca_preview: one photograph, as the store's cards carry (no mosaic).
  const preview = useCaPreview();
  const markFailed = (src: string) =>
    setFailed((f) => (f[src] ? f : { ...f, [src]: true }));

  const imgs = Array.from(
    new Set([heroImage, ...(route.collage_images || [])].filter(Boolean) as string[])
  ).filter((u) => !failed[u]);
  const extra = imgs.length - 3;

  const tile = (src: string, idx: number, cls: string) => (
    <div className={`relative overflow-hidden bg-gray-100 ${cls}`}>
      <img
        src={src}
        alt={`${route.name} — photo ${idx + 1}`}
        loading="lazy"
        onError={() => markFailed(src)}
        onLoad={(e) => { if (e.currentTarget.naturalWidth === 0) markFailed(src); }}
        className="w-full h-full object-cover transition-transform duration-[3000ms] ease-out group-hover:scale-105"
      />
      {idx === 2 && extra > 0 && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-sm font-extrabold">+{extra} photos</div>
      )}
    </div>
  );

  return (
    <a
      href="#"
      onClick={onClick}
      className="ca-route-card flex-none w-[330px] snap-start group cursor-pointer rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300"
    >
      <div className="relative h-44 bg-gray-100">
        {imgs.length === 0 ? (
          <div className={`w-full h-full bg-gradient-to-br ${getRouteGradient(route.route_type)}`} />
        ) : imgs.length === 1 || preview ? (
          tile(imgs[0], 0, 'w-full h-full')
        ) : (
          <div className="grid grid-cols-3 grid-rows-2 gap-0.5 h-full">
            {tile(imgs[0], 0, 'col-span-2 row-span-2')}
            {tile(imgs[1], 1, '')}
            {tile(imgs[2] || imgs[1], 2, '')}
          </div>
        )}
        <span className={`ca-route-chip absolute top-2.5 left-2.5 z-10 inline-flex items-center gap-1.5 text-[10.5px] font-extrabold tracking-wider uppercase text-white px-2.5 py-1.5 rounded-full backdrop-blur-sm ${getRouteChipBg(route.route_type)}`}>
          <span className="ca-route-emoji">{getRouteTypeIcon(route.route_type)}</span> {route.route_type} route
        </span>
        {route.passes_through_town && currentLocation && (
          <span className="ca-route-here absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1 text-[10.5px] font-bold text-green-900 bg-green-200/95 px-2.5 py-1.5 rounded-full shadow">
            📍 Passes through {currentLocation}
          </span>
        )}
        {route.collage_credits && route.collage_credits.length > 0 && imgs.length > 0 && (
          <span
            className="ca-route-credit absolute bottom-1.5 right-1.5 z-10 text-[9px] font-semibold text-white/95 bg-black/45 px-1.5 py-0.5 rounded"
            title={`Photos via Wikimedia Commons (CC): ${route.collage_credits.join(' · ')}`}
          >
            {preview ? (
              <span className="ca-route-credit__i" aria-label={`Photo credits: ${route.collage_credits.join(', ')}`}>©</span>
            ) : (
              <>
                <span className="ca-tc">📷 Wikimedia CC</span>
                <span className="ca-sc hidden">Photos: Wikimedia Commons</span>
              </>
            )}
          </span>
        )}
      </div>
      <div className="p-4 bg-white">
        <div className="text-xl font-extrabold text-gray-900 leading-tight">{route.name}</div>
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mt-0.5 mb-3">
          <span>{route.from}</span><span className="opacity-60">→</span><span>{route.to}</span>
        </div>
        <div className="flex items-stretch mb-3">
          <div className="flex-1 text-center px-1">
            <div className="text-base font-extrabold text-gray-900">{route.distance}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Distance</div>
          </div>
          <div className="flex-1 text-center px-1 border-l border-gray-200">
            <div className="text-base font-extrabold text-gray-900">{route.duration}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Drive</div>
          </div>
          {route.campsite_count !== undefined && route.campsite_count > 0 && (
            <div className="flex-1 text-center px-1 border-l border-gray-200">
              <div className="text-base font-extrabold text-gray-900">{route.campsite_count}</div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Campsites</div>
            </div>
          )}
        </div>
        <div className="ca-route-line relative h-16 rounded-lg bg-[#eaf1f6] border border-gray-200 overflow-hidden mb-3">
          <svg viewBox="0 0 320 64" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <path d="M18,46 C90,12 150,50 220,24 C260,8 290,22 304,18" fill="none" stroke="#0067B1" strokeWidth="3" strokeDasharray="2 4" strokeLinecap="round" opacity="0.55" />
            <circle cx="18" cy="46" r="4" fill="#0067B1" />
            <circle cx="304" cy="18" r="4" fill="#0067B1" />
            <circle cx="158" cy="35" r="6" fill="#2fa86a" stroke="#fff" strokeWidth="2" />
          </svg>
          <span className="absolute left-1.5 bottom-1.5 text-[9px] font-bold text-[#48607a] bg-white/85 px-1.5 rounded">{route.from}</span>
          <span className="absolute right-1.5 top-1.5 text-[9px] font-bold text-[#48607a] bg-white/85 px-1.5 rounded">{route.to}</span>
          {currentLocation && (
            <span className="absolute left-1/2 -translate-x-1/2 top-1 text-[9px] font-bold text-green-700 bg-white/90 px-1.5 rounded">You&apos;re here</span>
          )}
        </div>
        <p className="text-[12.5px] text-gray-700 leading-snug mb-2.5 line-clamp-2">{route.description}</p>
        {route.highlights && route.highlights.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {route.highlights.slice(0, 3).map((h, i) => (
              <span key={i} className="text-[11px] font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">{h}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between border-t border-gray-200 pt-3">
          <span
            role="button"
            tabIndex={0}
            onClick={onPlanTrip}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPlanTrip(e); }}
            className="text-sm font-extrabold text-[#1558b0] inline-flex items-center gap-1.5 hover:text-[#1558b0] cursor-pointer"
            title="Open the Trip Planner with this route pre-filled"
          >
            Plan this trip <span className="group-hover:translate-x-1 transition-transform">→</span>
          </span>
          {route.campsite_count !== undefined && route.campsite_count > 0 && (
            <span className="text-[11.5px] text-gray-500 font-semibold">{route.campsite_count} stops along the way</span>
          )}
        </div>
      </div>
    </a>
  );
}

export default function PopularRoutes({
  routes,
  currentLocation,
  heroImage,
  onRouteSelect
}: PopularRoutesProps): JSX.Element | null {
  const router = useRouter();
  const railRef = React.useRef<HTMLDivElement>(null);
  const scrollRail = (dir: number) =>
    railRef.current?.scrollBy({ left: dir * 350, behavior: 'smooth' });

  if (!routes || routes.length === 0) {
    return null;
  }

  const isCampsitePage: boolean = !!currentLocation;

  // Updated handler - navigate to home page with route params
  const handleRouteClick = (route: PopularRoute, e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    
    // If onRouteSelect callback provided (home page), use it
    if (onRouteSelect) {
      onRouteSelect(route.from, route.to, route.name);
      return;
    }
    
    // Otherwise navigate to home page with route parameters
    const params = new URLSearchParams({
      startPoint: route.from,
      endPoint: route.to,
      routeName: route.name,
      routeId: route.id,
      isRouteSearch: 'true'
    });

    router.push(withBase(`?${params.toString()}`));
  };

  // "Plan this trip" → open the Trip Planner with this road-trip pre-filled.
  const handlePlanTrip = (route: PopularRoute, e: React.SyntheticEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const params = new URLSearchParams({
      startPoint: route.from,
      endPoint: route.to,
      routeName: route.name,
      // The explorer plots the corridor only when this is set; the retired
      // wizard just pre-filled two text fields and made the user press on.
      isRouteSearch: 'true',
      plan: '1',
    });
    router.push(withBase(`/?${params.toString()}`));
  };

  if (!isCampsitePage) {
    // Home page layout
    return (
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Popular Road Trip Routes
            </h2>
            <p className="text-lg text-gray-600">
              Discover campsites along Australia's most iconic road trips
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {routes.map((route: PopularRoute) => (
              <a
                key={route.id}
                href="#"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleRouteClick(route, e)}
                className="group relative bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#1558b0] transition-colors">
                        {route.name}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center text-gray-600 mt-2">
                        <span className="font-medium">{route.from}</span>
                        <ArrowRightOutlined className="mx-0 sm:mx-2 text-sm rotate-90 sm:rotate-0" />
                        <span className="font-medium">{route.to}</span>
                      </div>
                    </div>
                    <div className="text-3xl">
                      {getRouteTypeIcon(route.route_type)}
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>🚗 {route.distance}</span>
                    <span>📅 {route.duration}</span>
                  </div>
                  
                  {route.campsite_count !== undefined && route.campsite_count > 0 && (
                    <div className="mt-3 text-sm text-gray-600">
                      <span className="font-medium">{route.campsite_count}+ campsites</span> along route
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <span className="text-sm text-[#1558b0] font-medium group-hover:underline">
                      Find campsites along this route →
                    </span>
                  </div>
                </div>
                
                <div className={`absolute inset-0 bg-gradient-to-br ${getRouteGradient(route.route_type)} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
              </a>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                // Scroll to search section for custom route
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex items-center px-6 py-3 bg-[#1558b0] text-white font-medium rounded-lg hover:bg-[#1558b0] transition-colors cursor-pointer"
            >
              <SwapOutlined className="mr-2" />
              Plan Your Own Route
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Campsite page layout
  return (
    <section className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold flex items-center">
          <FaRoute className="ca-pv-hide mr-2 text-[#1558b0]" />
          <CaseSwap title="Popular Routes Through Here" sentence="Road trips through here" />
        </h2>
        <span className="ca-pv-hide text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
          Road Trip Ideas
        </span>
      </div>

      <div className="relative">
        <div ref={railRef} className="pr-rail flex gap-4 overflow-x-auto pb-2 px-1 -mx-1 snap-x snap-mandatory scroll-smooth">
          {routes.map((route: PopularRoute) => (
            <RouteCard
              key={route.id}
              route={route}
              heroImage={heroImage}
              currentLocation={currentLocation}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleRouteClick(route, e)}
              onPlanTrip={(e) => handlePlanTrip(route, e)}
            />
          ))}
        </div>
        {routes.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => scrollRail(-1)}
              aria-label="Previous routes"
              className="hidden md:flex absolute left-1 top-[88px] -translate-y-1/2 z-20 w-9 h-9 items-center justify-center rounded-full bg-white/95 shadow-lg border border-gray-200 text-gray-700 hover:bg-white hover:scale-110 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button
              type="button"
              onClick={() => scrollRail(1)}
              aria-label="More routes"
              className="hidden md:flex absolute right-1 top-[88px] -translate-y-1/2 z-20 w-9 h-9 items-center justify-center rounded-full bg-white/95 shadow-lg border border-gray-200 text-gray-700 hover:bg-white hover:scale-110 transition"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </>
        )}
      </div>
      <style>{`.pr-rail::-webkit-scrollbar{display:none}.pr-rail{scrollbar-width:none;-ms-overflow-style:none}`}</style>

      <div className="mt-6 pt-6 border-t text-center">
        <button
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            router.push(withBase(''));
          }}
          className="text-[#1558b0] hover:text-[#123f7d] font-medium inline-flex items-center cursor-pointer"
        >
          Explore more road trip routes
          <span className="ml-1">→</span>
        </button>
      </div>
    </section>
  );
}